import React, { type FC } from "react";
import MytripsLoginModalContainer from "@components/AuthFlow/AuthenticationModal/MytripsLoginModalContainer";
import Footer from "@components/AuthFlow/Footer";
import type { PasswordlessBodyProps } from "@components/AuthFlow/Passwordless/contract";
import PasswordlessBody from "@components/AuthFlow/Passwordless/PasswordlessBody";
import { PASSWORDLESS_WITH_PASSWORD_CLICK_ID } from "@components/AuthFlow/Passwordless/PasswordlessContent";
import SigninBody from "@components/AuthFlow/Signin/SigninBody";
import ScreenProvider, {
  screenNames,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { useTranslation } from "@edreamsodigeo/frontend-core";
import { Body, Box, Flex, Heading } from "@edreamsodigeo/prisma-design-system";

import { useTools } from "../../../tools/useTools";
import { useMytripsLoginFlow } from "../useMyTripsLoginAuthFlow";
import type { MytripsLoginAuthFlowProps } from "./contract";
import MytripsLoginSigninHeader from "./MytripsLoginSigninHeader";

const { SIGNIN } = screenNames;

const MytripsLoginComponent: FC<MytripsLoginAuthFlowProps> = ({
  successRedirectionUrl,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useTools();
  const {
    origin,
    mytripsLoginFlow,
    email,
    setEmail,
    onClose,
    onSuccessNavigation,
    openForgotPasswordFlow,
    onRenderPasswordless,
    onRenderSignin,
    onRenderSuccessScreen,
    onRenderLoginSyncScreen,
    navigateToRegister,
  } = useMytripsLoginFlow(successRedirectionUrl);

  const passwordLessBodyProps: PasswordlessBodyProps = {
    signinLink: {
      content: t("authentication.edo.passwordless.withPasswordLabel"),
      trackingId: PASSWORDLESS_WITH_PASSWORD_CLICK_ID,
    },
    showSigninLink: true,
    signupLink: {
      title: t("authentication.signin.signup.title"),
      linkLabel: t("authentication.signin.signup.link"),
    },
    showSignupLink: true,
    submitButtonLabel: t("authentication.edo.passwordless.submit"),
    emailLabelForm: t("authentication.auth.form.email.label"),
    isLoginSyncEnabled: false,
    renderSuccessScreen: onRenderSuccessScreen,
    renderSigninScreen: onRenderSignin,
    renderSignupScreen: navigateToRegister,
    renderLoginSyncScreen: onRenderLoginSyncScreen,
  };

  return (
    <Box pt={isDesktop ? "big" : "3x_big"}>
      {mytripsLoginFlow !== SIGNIN && (
        <Box>
          <Flex
            flexDirection="column"
            alignItems="start"
            gap="base"
            mt="minus_3x_big"
          >
            {isDesktop && (
              <Heading
                type="2"
                weight="medium"
                textAlign="start"
                color="neutral.100"
              >
                {t("authentication.edo.passwordless.title")}
              </Heading>
            )}
            <Body type="small" color="neutral.80" textAlign="start">
              {t("authentication.edo.passwordless.subtitle")}
            </Body>
          </Flex>
          <PasswordlessBody {...passwordLessBodyProps} />
          <Footer withGeneralTC withRecaptchaPolicy />
        </Box>
      )}
      {mytripsLoginFlow === SIGNIN && (
        <Box>
          <MytripsLoginSigninHeader
            onSignupClick={navigateToRegister}
            trackingPrefix={origin}
          />
          <Flex flexDirection="column" gap="base">
            <SigninBody
              email={email}
              setEmail={setEmail}
              onPasswordlessClick={onRenderPasswordless}
              onForgotPasswordClick={openForgotPasswordFlow}
              closeScreen={onSuccessNavigation}
            />
            <Footer withGeneralTC withRecaptchaPolicy />
          </Flex>
        </Box>
      )}
      <MytripsLoginModalContainer closeModal={onClose} />
    </Box>
  );
};

const MytripsLoginAuthFlow: FC<MytripsLoginAuthFlowProps> = (props) => (
  <ScreenProvider /*gaTrackingPrefix={MYTRIPS_LOGIN_GA_TRACKING_PREFIX}*/>
    <MytripsLoginComponent {...props} />
  </ScreenProvider>
);

export default MytripsLoginAuthFlow;
